import React, { memo, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PT_TO_PX, Size } from './geometry';

/** Thumbnails are rendered once at ~50 dpi, half the base resolution. */
const THUMBNAIL_SCALE = 0.5;
const THUMBNAIL_WIDTH = 140;

type PdfThumbnailsProps = {
	doc: PDFDocumentProxy;
	pageSizes: Size[];
	currentPageIndex: number;
	/** Rendered images survive the panel closing and reopening. */
	cache: Map<number, string>;
	onSelectPage: (pageIndex: number) => void;
	onClose: () => void;
};

export const PdfThumbnails = (props: PdfThumbnailsProps) => {
	const listRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Escape closes only the drawer. Window capture runs before the document
		// capture listener Radix dialogs use, so a surrounding modal never sees it.
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			event.stopPropagation();
			props.onClose();
		};
		window.addEventListener('keydown', onKeyDown, { capture: true });
		return () =>
			window.removeEventListener('keydown', onKeyDown, { capture: true });
	}, [props.onClose]);

	useEffect(() => {
		listRef.current
			?.querySelector(
				`[data-thumbnail-index="${props.currentPageIndex}"]`,
			)
			?.scrollIntoView({ block: 'nearest' });
		// Only when the panel opens: following the page while it is open would fight
		// the user scrolling the list.
	}, []);

	return (
		<div
			className="absolute top-0 bottom-0 left-0 z-20 flex w-[188px] flex-col border-r border-[#ccc] bg-white shadow-lg"
			role="dialog"
			aria-label="Páginas del documento"
		>
			<div className="flex items-center justify-between border-b border-[#e5e5e5] px-3 py-2 text-sm font-semibold">
				Páginas
				<button
					type="button"
					aria-label="Cerrar páginas"
					className="rounded p-1 hover:bg-[#f0f0f0]"
					onClick={props.onClose}
				>
					<X size={16} />
				</button>
			</div>
			<div ref={listRef} className="flex-1 overflow-y-auto px-6 py-3">
				{props.pageSizes.map((size, index) => (
					<PdfThumbnail
						key={index}
						doc={props.doc}
						index={index}
						size={size}
						root={listRef}
						cache={props.cache}
						isCurrent={index === props.currentPageIndex}
						onSelect={props.onSelectPage}
					/>
				))}
			</div>
		</div>
	);
};

type PdfThumbnailProps = {
	doc: PDFDocumentProxy;
	index: number;
	size: Size;
	root: React.RefObject<HTMLDivElement>;
	cache: Map<number, string>;
	isCurrent: boolean;
	onSelect: (pageIndex: number) => void;
};

const PdfThumbnail = memo(function PdfThumbnail(props: PdfThumbnailProps) {
	const { doc, index, cache } = props;
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [src, setSrc] = useState<string | undefined>(() => cache.get(index));
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const element = buttonRef.current;
		if (src || !element) return;
		if (typeof IntersectionObserver === 'undefined') {
			setIsVisible(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ root: props.root.current, rootMargin: '200px 0px' },
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, [src, props.root]);

	useEffect(() => {
		if (!isVisible || src) return;
		let isCancelled = false;
		let cancelRender: (() => void) | undefined;
		(async () => {
			const page = await doc.getPage(index + 1);
			if (isCancelled) return;
			const viewport = page.getViewport({
				scale: PT_TO_PX * THUMBNAIL_SCALE,
			});
			const canvas = document.createElement('canvas');
			canvas.width = Math.ceil(viewport.width);
			canvas.height = Math.ceil(viewport.height);
			const context = canvas.getContext('2d');
			if (!context) return;
			const task = page.render({
				canvas,
				canvasContext: context,
				viewport,
			});
			cancelRender = () => task.cancel();
			await task.promise;
			const url = canvas.toDataURL('image/png');
			// Only the data URL is kept; free the bitmap now (see releaseCanvas).
			canvas.width = 0;
			canvas.height = 0;
			cache.set(index, url);
			if (!isCancelled) setSrc(url);
		})().catch(() => undefined);
		return () => {
			isCancelled = true;
			cancelRender?.();
		};
	}, [isVisible, src, doc, index, cache]);

	const height = (THUMBNAIL_WIDTH * props.size.height) / props.size.width;

	return (
		<button
			ref={buttonRef}
			type="button"
			data-thumbnail-index={index}
			aria-label={`Ir a la página ${index + 1}`}
			aria-current={props.isCurrent ? 'page' : undefined}
			onClick={() => props.onSelect(index)}
			className={cn(
				'relative mb-3 block bg-white outline-none',
				props.isCurrent
					? 'ring-2 ring-primary'
					: 'shadow-[0_0_0_1px_#ccc] hover:shadow-[0_0_0_1px_#888]',
			)}
			style={{ width: THUMBNAIL_WIDTH, height }}
		>
			{src && (
				<img
					src={src}
					alt=""
					draggable={false}
					className="block h-full w-full"
				/>
			)}
			<span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 text-[11px] leading-4 text-white">
				{index + 1}
			</span>
			{props.isCurrent && (
				<span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white">
					<Check size={10} />
				</span>
			)}
		</button>
	);
});
